import React, { FC, MutableRefObject, ReactElement, useEffect, useState } from 'react'
import { fromEvent, Subscription, zip } from 'rxjs'
import { takeWhile } from 'rxjs/operators'
import useIsMountedRef from 'use-is-mounted-ref'
import { EventNames } from '../../../NonReact/constants/EventNames'
import { getMaxMinRectFromElements } from '../../../NonReact/DOM/DOMUtils'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { StateAppender, useStateAppender } from '../../Common/Hooks/useStateAppender'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { useStateAsync } from '../../Common/Hooks/useStateAsync'
import { ReactState, ReactStateAsync } from '../../Common/Types/Types'
import { getTouringStep, incrementTouringStep, turnOffTour } from '../../Common/Utils/TourStorage'
import {
    intersectionObservable,
    mutationObservable,
    resizeObservable
} from '../../../NonReact/common/RxJS/RxJSUtilities'
import { CanvasOverlay } from '../Components/CanvasOverlay'
import { TourOverlay } from '../Components/TourOverlay'
import { TourEntry, TourOverlayPosition } from '../Types/Types'

type Props = {
    entries: TourEntry[]
    version: string
}

const TOUR_ACTIVE_BODY_CLASS: string = 'tour-active'
const DISABLE_IN_PRODUCT_EDUCATION: string = 'disable-in-product-education'

export const UserTour: FC<Props> = ({ entries, version }: Props): ReactElement => {
    const [currentEntry, setCurrentEntry]: ReactState<Nullable<number>> = useState<Nullable<number>>(
        getTouringStep(version)
    )
    const [currentElements, setCurrentElements, getCurrentElements]: ReactStateAsync<Nullable<HTMLElement[]>> =
        useStateAsync<Nullable<HTMLElement[]>>(null)
    const [currentBoundingRect, setCurrentBoundingRect, getBoundingClientRect]: ReactStateAsync<Nullable<DOMRect>> =
        useStateAsync<Nullable<DOMRect>>(null)
    const [currentEntries, setCurrentEntries]: ReactState<TourEntry[]> = useState<TourEntry[]>(entries)
    const [bodyRect, setBodyRect]: ReactState<DOMRect> = useState<DOMRect>(document.body.getBoundingClientRect())
    const [tourOverlayPosition, setTourOverlayPosition]: ReactState<Nullable<[TourOverlayPosition, DOMRect]>> =
        useState<Nullable<[TourOverlayPosition, DOMRect]>>()
    const {
        getter: getDestroyableSubscriptions,
        appender: appendToDestroyableSubscriptions
    }: StateAppender<Subscription> = useStateAppender<Subscription>([])
    const {
        appender: appendToDestroyableObserverables,
        getter: getDestroyableObserverables
    }: StateAppender<Subscription> = useStateAppender<Subscription>([])

    const isTourEnabled: Definable<boolean> = useFeatureFlag('newUserTour')

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const clearTour = (turnOff?: boolean): void => {
        if (isMounted.current) {
            setCurrentEntry(null)
            setCurrentBoundingRect(null)
            setCurrentEntries([])
        }

        getDestroyableSubscriptions().then((destroy: Subscription[]): void =>
            destroy.forEach((destroyableObserverable: Subscription): void => destroyableObserverable.unsubscribe())
        )
        getDestroyableObserverables().then((destroy: Subscription[]): void =>
            destroy.forEach((destroyableObserverable: Subscription): void => destroyableObserverable.unsubscribe())
        )

        document.body.classList.remove(TOUR_ACTIVE_BODY_CLASS)
        document.body.classList.remove(DISABLE_IN_PRODUCT_EDUCATION)

        if (turnOff) {
            turnOffTour(version)
        }
    }

    const tourNext = (): void => {
        getDestroyableObserverables().then((destroy: Subscription[]): void =>
            destroy.forEach((destroyableObserverable: Subscription): void => destroyableObserverable.unsubscribe())
        )

        if (isMounted.current) {
            if (!IsVarEmpty(currentEntry) && currentEntry < currentEntries.length) {
                const onNext: Nullable<() => void> = currentEntries[currentEntry]?.onNext

                if (isVarNotEmpty(onNext)) {
                    onNext()
                }
            }

            incrementTouringStep(version)
            setCurrentBoundingRect(null)
            setCurrentEntry((ce: Nullable<number>): Nullable<number> => {
                if (isVarNotEmpty(ce)) {
                    const nextEntry: number = ce + 1

                    if (nextEntry < currentEntries.length) {
                        return nextEntry
                    }

                    clearTour()

                    return undefined
                }

                return 0
            })
        }
    }

    const getEntry = (): void => {
        if (isVarNotEmpty(currentEntry)) {
            if (currentEntry >= currentEntries.length || currentEntry < 0) {
                return clearTour()
            }

            const thisEntry: TourEntry = currentEntries[currentEntry]

            document.body.classList.remove(DISABLE_IN_PRODUCT_EDUCATION)

            if (thisEntry.disablePopoverViewable) {
                document.body.classList.remove(TOUR_ACTIVE_BODY_CLASS)
            } else {
                document.body.classList.add(TOUR_ACTIVE_BODY_CLASS)
            }

            appendToDestroyableObserverables(
                zip(...thisEntry.elements())
                    .pipe(
                        takeWhile(
                            (elsContainers: HTMLElement[][]): boolean =>
                                elsContainers.some((els: HTMLElement[]): boolean => els.length === 0),
                            true
                        )
                    )
                    .subscribe((elements: HTMLElement[][]): void => {
                        if (isMounted.current) {
                            const flattenedElements: HTMLElement[] = elements.reduce(
                                (acc: HTMLElement[], val: HTMLElement[]): HTMLElement[] => acc.concat(val),
                                []
                            )
                            const elementsToUse: HTMLElement[] = thisEntry.elementLookup
                                ? (flattenedElements
                                      .map(thisEntry.elementLookup)
                                      .filter((e: Nullable<HTMLElement>): boolean => isVarNotEmpty(e)) as HTMLElement[])
                                : flattenedElements

                            if (elementsToUse.length === 0) {
                                setCurrentBoundingRect(null)
                            } else {
                                document.body.classList.add(DISABLE_IN_PRODUCT_EDUCATION)
                            }

                            const subscribeCallback = (): void => {
                                if (isMounted.current) {
                                    setCurrentBoundingRect(getMaxMinRectFromElements(elementsToUse))
                                }
                            }

                            elementsToUse.forEach((element: HTMLElement): void => {
                                if (thisEntry.interceptClick) {
                                    appendToDestroyableObserverables(
                                        fromEvent(element, EventNames.CLICK).subscribe((): void => {
                                            if (isMounted.current) {
                                                tourNext()
                                            }
                                        })
                                    )
                                }

                                appendToDestroyableObserverables(
                                    resizeObservable(element).subscribe(subscribeCallback),
                                    intersectionObservable(element).subscribe(subscribeCallback),
                                    thisEntry.watchAttributeChanges
                                        ? mutationObservable(element, { attributes: true }).subscribe(subscribeCallback)
                                        : undefined
                                )
                            })

                            if (thisEntry.potentialTransitionLag) {
                                setTimeout((): void => subscribeCallback(), thisEntry.potentialTransitionLag)
                            }

                            if (elementsToUse.length > 0) {
                                if (thisEntry.containingScrollableAreaSelector) {
                                    const containingScrollableArea: OnlyNullable<HTMLElement> =
                                        elementsToUse[0].closest(thisEntry.containingScrollableAreaSelector)

                                    if (containingScrollableArea) {
                                        appendToDestroyableObserverables(
                                            resizeObservable(containingScrollableArea).subscribe(subscribeCallback),
                                            fromEvent(containingScrollableArea, EventNames.SCROLL).subscribe(
                                                subscribeCallback
                                            )
                                        )
                                    }
                                }

                                setCurrentElements(elementsToUse)
                                setCurrentBoundingRect(getMaxMinRectFromElements(elementsToUse))
                            }
                        }
                    })
            )

            if (thisEntry.skipToNext) {
                appendToDestroyableObserverables(
                    thisEntry.skipToNext.subscribe((res: Voidable<boolean>): void => {
                        if (res || res === undefined) {
                            tourNext()
                        }
                    })
                )
            }

            if (thisEntry.skipNext) {
                appendToDestroyableObserverables(
                    thisEntry.skipNext.subscribe((res: boolean): void => {
                        if (res) {
                            tourNext()
                            tourNext()
                        }
                    })
                )
            }
        }

        return undefined
    }

    const getUpdatedBounds = (): void => {
        if (isMounted.current) {
            getCurrentElements().then((currElements: Nullable<HTMLElement[]>): void => {
                getBoundingClientRect().then((cbr: Nullable<DOMRect>): void => {
                    if (isMounted.current) {
                        setCurrentBoundingRect(cbr === null ? null : getMaxMinRectFromElements(currElements))
                    }
                })
            })
        }
    }

    useEffect((): void => {
        if (currentEntries && currentEntries.length > 0) {
            appendToDestroyableSubscriptions(
                resizeObservable(document.body).subscribe((): void => {
                    if (isMounted.current) {
                        setBodyRect(document.body.getBoundingClientRect())
                    }

                    getUpdatedBounds()
                }),
                fromEvent(window, EventNames.SCROLL).subscribe(getUpdatedBounds)
            )
        }
    }, [currentEntries])

    useEffect(getEntry, [currentEntry])

    return isTourEnabled &&
        isVarNotEmpty(currentEntry) &&
        isVarNotEmpty(currentBoundingRect) &&
        currentElements &&
        currentElements.length > 0 ? (
        <>
            <CanvasOverlay
                tourEntry={currentEntries[currentEntry]}
                boundingRect={currentBoundingRect}
                bodyRect={bodyRect}
                spacingOffset={currentEntries[currentEntry]?.disableGutter ? 0 : 10}
                preferredPosition={currentEntries[currentEntry]?.preferredPosition}
                setTourOverlayPosition={setTourOverlayPosition}
            />
            {tourOverlayPosition ? (
                <TourOverlay
                    tourPosition={tourOverlayPosition}
                    rect={currentBoundingRect}
                    tourEntry={currentEntries[currentEntry]}
                    nextTour={tourNext}
                    cancelTour={clearTour}
                />
            ) : (
                <> </>
            )}
        </>
    ) : (
        <></>
    )
}
